import { useState } from "react";
import { Film, Loader2, Copy, ExternalLink, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { analyzeVideo } from "../lib/api";
import PostKit from "../components/PostKit";
import { T } from "@/locales/tr";

const PRELOADED = [
  "https://www.facebook.com/share/1EdZFCYrKs/?mibextid=wwXIfr",
  "https://www.facebook.com/share/1JWahW3mP2/?mibextid=wwXIfr",
  "https://www.facebook.com/share/1HSdrKM3mc/?mibextid=wwXIfr",
  "https://www.facebook.com/share/1bCehnqr8S/?mibextid=wwXIfr",
  "https://www.facebook.com/share/1JP2yfXS31/?mibextid=wwXIfr",
  "https://www.facebook.com/share/1HSg5G6GDA/?mibextid=wwXIfr",
  "https://www.facebook.com/share/18UMimJwoN/?mibextid=wwXIfr",
];

const COUNTRIES = ["Sénégal", "France", "Union européenne", "États-Unis", "Côte d'Ivoire", "Maroc", "ONU (droit international)"];

export default function Analyse() {
  const [items, setItems] = useState(PRELOADED.map((u) => ({ uid: crypto.randomUUID(), url: u, transcript: "", notes: "", country: "Sénégal", result: "", loading: false })));

  const updateItem = (i, key, value) =>
    setItems((arr) => arr.map((x, idx) => idx === i ? { ...x, [key]: value } : x));

  const addItem = () => setItems((a) => [...a, { uid: crypto.randomUUID(), url: "", transcript: "", notes: "", country: "Sénégal", result: "", loading: false }]);
  const removeItem = (i) => setItems((a) => a.filter((_, idx) => idx !== i));

  const analyze = async (i) => {
    const it = items[i];
    if (!it.url.trim()) { toast.error(T("Mets une URL de vidéo.")); return; }
    updateItem(i, "loading", true);
    updateItem(i, "result", "");
    try {
      const data = await analyzeVideo({ url: it.url, transcript: it.transcript, country: it.country, notes: it.notes });
      updateItem(i, "result", data.text);
      toast.success(T("Analyse prête."));
    } catch { toast.error(T("L'analyse a échoué.")); }
    finally { updateItem(i, "loading", false); }
  };

  return (
    <div data-testid="page-analyse" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Analyse vidéo & post")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {T("Décrypte. Cite la loi.")}<br />
        <span className="italic font-light">{T("Réponds dans les commentaires.")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-4 max-w-3xl leading-relaxed">
        {T("Pour chaque vidéo Facebook (ou autre), Papicoo livre :")} <strong>{T("sujet")}</strong>, <strong>{T("décodage du fond")}</strong>, <strong>{T("preuve constitutionnelle")}</strong> {T("selon le pays,")} <strong>{T("valeurs abrahamiques")}</strong> {T("(millat Ibrâhîm),")} <strong>{T("preuve coranique extraordinaire")}</strong> {T("et")} <strong>{T("3 commentaires prêts à coller")}</strong>{T(". Sage, discipliné, rassurant — toujours politiquement correct.")}
      </p>

      <div className="mt-3 papicoo-card p-4 text-xs text-[color:var(--papicoo-muted)] leading-relaxed">
        {T("⚠️ Facebook empêche le visionnage automatique des vidéos privées/partagées. Pour une analyse de précision, colle le")} <strong>{T("résumé ou la transcription")}</strong> {T("dans le champ prévu. Sans transcription, Papicoo propose un cadre d'analyse général à partir de l'URL.")}
      </div>

      <div className="mt-8 space-y-5">
        {items.map((it, i) => (
          <div key={it.uid} data-testid={`analyse-item-${i}`} className="papicoo-card p-5 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--papicoo-muted)]">Vidéo #{i + 1}</div>
              {items.length > 1 && (
                <button onClick={() => removeItem(i)} data-testid={`remove-${i}`} className="text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-accent)]">
                  <Trash2 size={16} />
                </button>
              )}
            </div>

            <div className="mt-3 grid sm:grid-cols-[1fr_180px] gap-3">
              <input
                data-testid={`url-${i}`}
                value={it.url}
                onChange={(e) => updateItem(i, "url", e.target.value)}
                placeholder="https://www.facebook.com/share/…"
                className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
              />
              <select
                data-testid={`country-${i}`}
                value={it.country}
                onChange={(e) => updateItem(i, "country", e.target.value)}
                className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
              >
                {COUNTRIES.map((c) => <option key={c} value={c}>{T(c)}</option>)}
              </select>
            </div>

            <textarea
              data-testid={`transcript-${i}`}
              value={it.transcript}
              onChange={(e) => updateItem(i, "transcript", e.target.value)}
              rows={3}
              placeholder={T("Colle un résumé ou la transcription si tu l'as (optionnel).")}
              className="w-full mt-3 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
            />
            <input
              data-testid={`notes-${i}`}
              value={it.notes}
              onChange={(e) => updateItem(i, "notes", e.target.value)}
              placeholder={T("Notes / contexte (qui parle, qui partage, ton du fil…) — optionnel")}
              className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
            />

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button data-testid={`analyze-${i}`} onClick={() => analyze(i)} disabled={it.loading} className="inline-flex items-center gap-2 px-5 py-3 rounded-full papicoo-btn-primary disabled:opacity-50">
                {it.loading ? <Loader2 size={16} className="animate-spin" /> : <Film size={16} />}
                {it.loading ? T("Analyse en cours…") : T("Analyser cette vidéo")}
              </button>
              {it.url && (
                <a href={it.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs papicoo-link-underline text-[color:var(--papicoo-ink)]">
                  {T("Ouvrir la vidéo")} <ExternalLink size={12} />
                </a>
              )}
            </div>

            {it.result && (
              <div className="mt-5 border-t border-[color:var(--papicoo-border)] pt-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display text-lg text-[color:var(--papicoo-ink)]">{T("Analyse Papicoo")}</div>
                  <button data-testid={`copy-${i}`} onClick={() => { navigator.clipboard.writeText(it.result); toast.success(T("Copié.")); }} className="text-xs inline-flex items-center gap-1 text-[color:var(--papicoo-muted)] hover:text-[color:var(--papicoo-ink)]">
                    <Copy size={14} /> {T("Copier")}
                  </button>
                </div>
                <pre className="whitespace-pre-wrap font-sans text-[14px] leading-relaxed">{it.result}</pre>
                <PostKit text={it.result} title={T("Joins la photo officielle au commentaire")} />
              </div>
            )}
          </div>
        ))}
      </div>

      <button data-testid="add-item" onClick={addItem} className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[color:var(--papicoo-border)] text-sm text-[color:var(--papicoo-ink)] hover:border-[color:var(--papicoo-ink)]">
        <Plus size={14} /> {T("Ajouter une vidéo")}
      </button>
    </div>
  );
}
